import clsx from "clsx";
import { FiEye, FiEyeOff, FiX } from "react-icons/fi";
import { IS_MIX_CATALOG } from "@/config/browserBoard";
import type { ThreadSummary } from "@/entities/thread";
import { useCatalogStore } from "@/features/catalog/stores/catalogStore";
import { getTagFilterGroups } from "@/features/catalog/utils/tagFilterGroups";
import { MixSettings } from "@/features/settings/components/sections/MixSettings";

interface TagFilterProps {
  threads: ThreadSummary[];
  className?: string;
}

export const TagFilter: React.FunctionComponent<TagFilterProps> = ({
  threads,
  className,
}: TagFilterProps) => {
  const {
    selectedTag,
    hiddenTags,
    setSelectedTag,
    addHiddenTag,
    removeHiddenTag,
    clearHiddenTags,
  } = useCatalogStore();
  const { categoryTags, unavailableHiddenTags } = getTagFilterGroups(
    threads.map((thread) => thread.tags),
    hiddenTags,
  );

  if (
    !IS_MIX_CATALOG &&
    categoryTags.length === 0 &&
    unavailableHiddenTags.length === 0
  ) {
    return null;
  }

  return (
    <TagFilterView
      isMixCatalog={IS_MIX_CATALOG}
      categoryTags={categoryTags}
      unavailableHiddenTags={unavailableHiddenTags}
      selectedTag={selectedTag}
      hiddenTags={hiddenTags}
      setSelectedTag={setSelectedTag}
      addHiddenTag={addHiddenTag}
      removeHiddenTag={removeHiddenTag}
      clearHiddenTags={clearHiddenTags}
      className={className}
    />
  );
};

type TagFilterViewProps = {
  isMixCatalog?: boolean;
  selectedTag: string | null;
  hiddenTags: string[];
  setSelectedTag: (tag: string | null) => void;
  addHiddenTag: (tag: string) => void;
  removeHiddenTag: (tag: string) => void;
  clearHiddenTags: () => void;
  categoryTags: string[];
  unavailableHiddenTags?: string[];
  className?: string;
};

export function TagFilterView({
  isMixCatalog = false,
  selectedTag,
  hiddenTags,
  setSelectedTag,
  addHiddenTag,
  removeHiddenTag,
  clearHiddenTags,
  categoryTags,
  unavailableHiddenTags = [],
  className,
}: TagFilterViewProps): React.ReactElement {
  const hiddenTagNames = new Set(hiddenTags);
  const visibleCategoryTags = categoryTags.filter(
    (tag) => !hiddenTagNames.has(tag),
  );
  return (
    <fieldset
      className={clsx(
        "flex min-w-0 flex-col gap-3 overflow-x-visible px-2 pb-2",
        className,
      )}
      aria-label="カテゴリ絞り込み"
    >
      {isMixCatalog && (
        <details
          open
          className="rounded-lg border border-border bg-transparent"
        >
          <summary className="min-h-11 cursor-pointer px-3 py-2 text-sm font-semibold leading-7 text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary">
            統合カタログ
          </summary>
          <section
            className="border-t border-border"
            aria-label="統合カタログの板設定"
          >
            <MixSettings />
          </section>
        </details>
      )}
      <TagFilterGroup
        title="カテゴリ一覧"
        emptyMessage={
          categoryTags.length === 0
            ? "カテゴリはありません"
            : "表示中のカテゴリはありません"
        }
        tags={visibleCategoryTags}
        selectedTag={selectedTag}
        setSelectedTag={setSelectedTag}
        className="border-border bg-transparent"
      />
      <TagVisibilitySettings
        categoryTags={categoryTags}
        unavailableHiddenTags={unavailableHiddenTags}
        hiddenTagNames={hiddenTagNames}
        hiddenTagCount={hiddenTags.length}
        addHiddenTag={addHiddenTag}
        removeHiddenTag={removeHiddenTag}
        clearHiddenTags={clearHiddenTags}
      />
    </fieldset>
  );
}

type TagFilterGroupProps = {
  title: string;
  emptyMessage: string;
  tags: string[];
  selectedTag: string | null;
  setSelectedTag: (tag: string | null) => void;
  className?: string;
};

function TagFilterGroup({
  title,
  emptyMessage,
  tags,
  selectedTag,
  setSelectedTag,
  className,
}: TagFilterGroupProps): React.ReactElement {
  return (
    <details open className={clsx("min-w-0 rounded-lg border", className)}>
      <summary className="min-h-11 cursor-pointer px-3 py-2 text-sm font-semibold leading-7 text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary">
        {title}
      </summary>
      <section
        className="border-t border-border p-3"
        aria-label={`${title}の選択`}
      >
        <ul
          className="category-filter-options m-0 flex list-none flex-wrap gap-2 p-0"
          aria-label={title}
        >
          <li className="inline-flex">
            <button
              type="button"
              aria-pressed={selectedTag === null}
              className={clsx(
                "min-h-11 rounded-md border px-3 py-1 text-xs font-medium focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary",
                selectedTag === null
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border bg-background text-foreground",
              )}
              onClick={(): void => setSelectedTag(null)}
            >
              すべて
            </button>
          </li>
          {tags.map((tag) => (
            <li key={tag} className="inline-flex">
              <button
                type="button"
                aria-pressed={selectedTag === tag}
                className={clsx(
                  "min-h-11 rounded-md border px-3 py-1 text-xs font-medium focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary",
                  selectedTag === tag
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border bg-background text-foreground",
                )}
                onClick={(): void => {
                  // 選択中のカテゴリを再度押すと絞り込みを解除する。
                  setSelectedTag(selectedTag === tag ? null : tag);
                }}
              >
                {tag}
              </button>
            </li>
          ))}
        </ul>
        {tags.length === 0 && (
          <p className="text-xs text-muted-foreground">{emptyMessage}</p>
        )}
      </section>
    </details>
  );
}

type TagVisibilitySettingsProps = {
  categoryTags: string[];
  unavailableHiddenTags: string[];
  hiddenTagNames: ReadonlySet<string>;
  hiddenTagCount: number;
  addHiddenTag: (tag: string) => void;
  removeHiddenTag: (tag: string) => void;
  clearHiddenTags: () => void;
};

function TagVisibilitySettings({
  categoryTags,
  unavailableHiddenTags,
  hiddenTagNames,
  hiddenTagCount,
  addHiddenTag,
  removeHiddenTag,
  clearHiddenTags,
}: TagVisibilitySettingsProps): React.ReactElement {
  return (
    <details className="rounded-lg border border-border bg-muted/20">
      <summary className="min-h-11 cursor-pointer px-3 py-2 text-sm font-semibold leading-7 text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary">
        非表示カテゴリ設定
      </summary>
      <div className="border-t border-border p-3">
        <p className="mb-3 text-xs text-muted-foreground">
          一覧から外すカテゴリをここで設定できます。
        </p>
        <div className="flex flex-col gap-3">
          <TagVisibilityGroup
            title="カテゴリ"
            tags={categoryTags}
            hiddenTagNames={hiddenTagNames}
            addHiddenTag={addHiddenTag}
            removeHiddenTag={removeHiddenTag}
          />
          {unavailableHiddenTags.length > 0 && (
            <TagVisibilityGroup
              title="現在の一覧にない非表示カテゴリ"
              tags={unavailableHiddenTags}
              hiddenTagNames={hiddenTagNames}
              addHiddenTag={addHiddenTag}
              removeHiddenTag={removeHiddenTag}
            />
          )}
        </div>
        {hiddenTagCount > 0 && (
          <button
            type="button"
            className="mt-3 flex min-h-11 items-center justify-center gap-1 rounded-md border border-secondary bg-background px-3 py-1 text-xs text-secondary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
            onClick={clearHiddenTags}
          >
            <FiX aria-hidden="true" />
            非表示をすべて解除
          </button>
        )}
      </div>
    </details>
  );
}

type TagVisibilityGroupProps = {
  title: string;
  tags: string[];
  hiddenTagNames: ReadonlySet<string>;
  addHiddenTag: (tag: string) => void;
  removeHiddenTag: (tag: string) => void;
};

function TagVisibilityGroup({
  title,
  tags,
  hiddenTagNames,
  addHiddenTag,
  removeHiddenTag,
}: TagVisibilityGroupProps): React.ReactElement | null {
  if (tags.length === 0) return null;

  return (
    <div>
      <p className="mb-2 text-xs font-semibold text-muted-foreground">
        {title}
      </p>
      <ul
        className="m-0 flex list-none flex-wrap gap-2 p-0"
        aria-label={`${title}の非表示設定`}
      >
        {tags.map((tag) => {
          const isHidden = hiddenTagNames.has(tag);
          return (
            <li key={tag}>
              <button
                type="button"
                aria-pressed={isHidden}
                data-filter-state={isHidden ? "hidden" : "visible"}
                className={clsx(
                  "flex min-h-11 items-center gap-2 rounded-md border px-3 py-1 text-xs focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary",
                  isHidden
                    ? "border-secondary bg-secondary text-secondary-foreground"
                    : "border-border bg-background text-muted-foreground",
                )}
                onClick={(): void =>
                  isHidden ? removeHiddenTag(tag) : addHiddenTag(tag)
                }
              >
                {isHidden ? (
                  <FiEye aria-hidden="true" />
                ) : (
                  <FiEyeOff aria-hidden="true" />
                )}
                <span>{tag}</span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
